import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { getNumberFormatter } from "@/lib/utils/getNumberFormatter";
import { Row } from "@tanstack/react-table";
import {
  ReadProjectSettlements,
  PERIOD_STATUS,
} from "@/schemas/projectSettlements";
import { EnumFilter } from "@/components/data-table/components/EnumFilter";
import { PROJECT_STATUS } from "@/schemas/project";

interface NumericGetValue {
  getValue: () => number;
}

export function useProjectSettlementsListColumnOverrides() {
  const { i18n } = useTranslation();
  const numberFormatter = useMemo(
    () => getNumberFormatter(i18n.language),
    [i18n.language],
  );

  return useMemo(
    () => ({
      project_status: {
        id: "project_status",
        cell: ({ row }: { row: Row<ReadProjectSettlements> }) =>
          i18n.t(`projectStatuses.${row.original.project_status}`),
        meta: {
          filter: {
            component: EnumFilter,
            props: {
              options: PROJECT_STATUS,
              translationKey: "projectStatuses",
            },
          },
        },
      },
      period_status: {
        id: "period_status",
        cell: ({ row }: { row: Row<ReadProjectSettlements> }) =>
          i18n.t(`periodStatuses.${row.original.period_status}`),
        meta: {
          filter: {
            component: EnumFilter,
            props: {
              options: PERIOD_STATUS,
              translationKey: "periodStatuses",
            },
          },
        },
      },
      planned_hours: {
        id: "planned_hours",
        cell: ({ getValue }: NumericGetValue) => numberFormatter(getValue()),
      },
      planned_avg_rate: {
        id: "planned_avg_rate",
        cell: ({ getValue }: NumericGetValue) => numberFormatter(getValue()),
      },
      planned_income: {
        id: "planned_income",
        cell: ({ getValue }: NumericGetValue) => numberFormatter(getValue()),
      },
      scheduled_hours: {
        id: "scheduled_hours",
        cell: ({ getValue }: NumericGetValue) => numberFormatter(getValue()),
      },
      scheduled_avg_rate: {
        id: "scheduled_avg_rate",
        cell: ({ getValue }: NumericGetValue) => numberFormatter(getValue()),
      },
      scheduled_payment: {
        id: "scheduled_payment",
        cell: ({ getValue }: NumericGetValue) => numberFormatter(getValue()),
      },
      invoiced_hours: {
        id: "invoiced_hours",
        cell: ({ getValue }: NumericGetValue) => numberFormatter(getValue()),
      },
      invoiced_avg_rate: {
        id: "invoiced_avg_rate",
        cell: ({ getValue }: NumericGetValue) => numberFormatter(getValue()),
      },
      invoiced_income: {
        id: "invoiced_income",
        cell: ({ getValue }: NumericGetValue) => numberFormatter(getValue()),
      },
      contractors_hours: {
        id: "contractors_hours",
        cell: ({ getValue }: NumericGetValue) => numberFormatter(getValue()),
      },
      contractors_avg_rate: {
        id: "contractors_avg_rate",
        cell: ({ getValue }: NumericGetValue) => numberFormatter(getValue()),
      },
      contractors_payment: {
        id: "contractors_payment",
        cell: ({ getValue }: NumericGetValue) => numberFormatter(getValue()),
      },
      planned_profitability: {
        id: "planned_profitability",
        cell: ({ getValue }: NumericGetValue) => numberFormatter(getValue()),
      },
      profitability: {
        id: "profitability",
        cell: ({ getValue }: NumericGetValue) => numberFormatter(getValue()),
      },
    }),
    [i18n, numberFormatter],
  );
}
